// Angular
import { Injectable } from '@angular/core';

// Third party
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Settings card identifier — null means no card open
 */
export type AccountSettingsCard =
  | 'wageringPreferences'
  | 'language'
  | 'wageringLimits'
  | 'changePassword'
  | 'favoriteTracks'
  | null;

/**
 * Static descriptor used to render a settings row
 */
export interface AccountSettingsCardDescriptor {
  id: Exclude<AccountSettingsCard, null>;
  label: string;
  // Language-pack key; `label` is the fallback when a pack omits it
  labelKey: string;
  iconLight: string;
  iconDark: string;
  dataCy: string;
  expandable: boolean;
}

// AmWagerTheme codes: 3 = Light, 4 = Dark
export const AM_WAGER_THEME_DARK = 4;
export const AM_WAGER_THEME_LIGHT = 3;

// localStorage key for the dark-mode toggle
export const DARK_MODE_STORAGE_KEY = 'darkModeActivated';

// Message shown for rows that are not yet available
export const ACCOUNT_SETTINGS_COMING_SOON_MSG =
  "We're working hard to bring this feature to you! Stay tuned for updates.";

/**
 * State and pure helpers for the AccountSettings component
 */
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  private readonly _activeCard$ = new BehaviorSubject<AccountSettingsCard>(null);
  private readonly _darkMode$ = new BehaviorSubject<boolean>(false);

  readonly activeCard$: Observable<AccountSettingsCard> =
    this._activeCard$.asObservable();

  readonly darkMode$: Observable<boolean> = this._darkMode$.asObservable();

  readonly cards: AccountSettingsCardDescriptor[] = [
    {
      id: 'wageringPreferences',
      label: 'Wagering Preferences',
      labelKey: 'account.preferences.wagering',
      iconLight: '/assets/images/account_settings/wagering_preferences_icon_light.svg',
      iconDark: '/assets/images/account_settings/wagering_preferences_icon_dark.svg',
      dataCy: 'wagering-preferences',
      expandable: true
    },
    {
      id: 'language',
      label: 'Language',
      labelKey: 'account.preferences.language',
      iconLight: '/assets/images/account_settings/language_icon_light.svg',
      iconDark: '/assets/images/account_settings/language_icon_dark.svg',
      dataCy: 'settings-language',
      expandable: true
    },
    {
      id: 'wageringLimits',
      label: 'Wagering Limits',
      labelKey: 'account.preferences.wageringlimits',
      iconLight: '/assets/images/account_settings/wagering_limits_icon_light.png',
      iconDark: '/assets/images/account_settings/wagering_limits_icon_dark.png',
      dataCy: 'wagering-limits',
      expandable: true
    },
    {
      id: 'changePassword',
      label: 'Change Password',
      labelKey: 'account.change.password',
      iconLight: '/assets/images/account_settings/change_password_icon_light.svg',
      iconDark: '/assets/images/account_settings/change_password_icon_dark.svg',
      dataCy: 'settings-change-pw',
      expandable: true
    },
    {
      id: 'favoriteTracks',
      label: 'Favorite Tracks',
      labelKey: 'account.preferences.favoritetracks',
      iconLight: '/assets/images/account_settings/favorite_tracks_icon_light.svg',
      iconDark: '/assets/images/account_settings/favorite_tracks_icon_dark.svg',
      dataCy: 'favorite-tracks',
      expandable: true
    }
  ];

  get activeCard(): AccountSettingsCard {
    return this._activeCard$.getValue();
  }

  get darkMode(): boolean {
    return this._darkMode$.getValue();
  }

  /**
   * Toggle a card open/closed (accordion behaviour)
   * @param {AccountSettingsCard} card Card identifier, or null to close all
   * @returns {void}
   */
  toggleCard(card: AccountSettingsCard): void {
    if (this._activeCard$.getValue() === card) {
      this._activeCard$.next(null);
      return;
    }
    this._activeCard$.next(card);
  }

  /**
   * Force-close every card
   * @returns {void}
   */
  closeAllCards(): void {
    this._activeCard$.next(null);
  }

  /**
   * Update dark-mode flag, body class and localStorage
   * @param {boolean} mode True for dark, false for light
   * @returns {void}
   */
  applyDarkMode(mode: boolean): void {
    this._darkMode$.next(mode);

    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.toggle('dark', mode);
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DARK_MODE_STORAGE_KEY, mode ? 'true' : 'false');
    }
  }

  /**
   * Read the persisted dark-mode flag from localStorage
   * @returns {boolean} Persisted dark-mode flag (false when missing)
   */
  readPersistedDarkMode(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true';
  }

  /**
   * Convert AmWagerTheme code to dark-mode flag
   * @param {number} amWagerTheme Value from AccountPreferences.AmWagerTheme
   * @returns {boolean} True if the user has chosen dark mode
   */
  themeFromPreference(amWagerTheme?: number): boolean {
    return amWagerTheme === AM_WAGER_THEME_DARK;
  }

  /**
   * Convert dark-mode flag to the AmWagerTheme string code
   * @param {boolean} darkMode Current dark-mode flag
   * @returns {string} '4' for dark, '3' for light
   */
  themeCodeFromMode(darkMode: boolean): string {
    return darkMode ? String(AM_WAGER_THEME_DARK) : String(AM_WAGER_THEME_LIGHT);
  }
}
